import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { SITE } from "@/constants/testIds";
import { apiClient, API } from "@/lib/api";
import { SECTION_MAP } from "@/lib/sections";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { ReportCard } from "@/components/site/ReportCard";
import { ReportHeader } from "@/components/site/ReportHeader";
import { ReportSummary } from "@/components/site/ReportSummary";
import { ReportMetrics } from "@/components/site/ReportMetrics";
import { ReportHighlightPanels, ReportKeyHighlights } from "@/components/site/ReportHighlightPanels";
import { ReportPdfSection } from "@/components/site/ReportPdfSection";

function fileUrl(path) {
  return path ? `${API.replace("/api", "")}${path}` : null;
}

// Hoisted so framer-motion gets a stable object reference across re-renders.
const BODY_MOTION = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4 } };

export default function ReportPage() {
  const { slug } = useParams();
  const [report, setReport] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setReport(null);
    setNotFound(false);
    window.scrollTo(0, 0);
    apiClient
      .get(`/public/reports/${slug}`)
      .then(({ data }) => {
        setReport(data);
        document.title = `${data.company_name} — KIRA Score ${data.kira_score} | KIRA Score`;
      })
      .catch(() => setNotFound(true));
    return () => {
      document.title = "KIRA Score — Research Before Returns";
    };
  }, [slug]);

  // Derived, filtered lists — memoized so they're only recomputed when the report changes.
  const highlights = useMemo(() => (report?.key_highlights || []).filter(Boolean), [report]);
  const strengths = useMemo(() => (report?.strengths || []).filter(Boolean), [report]);
  const risks = useMemo(() => (report?.risks || []).filter(Boolean), [report]);
  const metrics = useMemo(() => (report?.key_metrics || []).filter((m) => m && m.label && m.value), [report]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-kira-bg text-white">
        <SiteNav />
        <div className="max-w-3xl mx-auto px-4 py-32 text-center">
          <h1 className="font-chivo text-2xl font-bold mb-3">Report not found</h1>
          <p className="text-kira-textMuted text-sm mb-6">
            This research note may have been unpublished or moved.
          </p>
          <Link to="/" className="text-sm font-medium text-kira-gold hover:underline">
            Back to KIRA Score
          </Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-kira-bg text-white">
        <SiteNav />
        <p className="text-center text-sm text-kira-textMuted py-32">Loading research…</p>
      </div>
    );
  }

  const section = SECTION_MAP[report.section];
  const pdf = fileUrl(report.pdf_url);
  const logo = fileUrl(report.logo_url);
  const cover = fileUrl(report.cover_url);

  const share = async () => {
    const shareUrl = `${API}/s/${report.slug}`;
    const title = `${report.company_name} — KIRA Score ${report.kira_score} | KIRA Score`;
    if (navigator.share) {
      try {
        await navigator.share({ title, url: shareUrl });
        return;
      } catch (err) {
        // User dismissed the native share sheet — fall through to clipboard copy.
        console.debug("Native share cancelled or unavailable:", err);
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied — paste it on WhatsApp, X or LinkedIn");
    } catch {
      toast.error("Could not copy the link");
    }
  };

  return (
    <div className="min-h-screen bg-kira-bg text-white">
      <SiteNav />
      <div data-testid={SITE.reportPage} className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <Link
          to={`/research/${report.section}`}
          className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-kira-textMuted hover:text-kira-gold mb-8 transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> {section?.label || "Research"}
        </Link>

        <motion.div {...BODY_MOTION}>
          <ReportHeader report={report} logo={logo} onShare={share} />
          <ReportSummary summary={report.summary} />
          <ReportHighlightPanels strengths={strengths} risks={risks} />
          <ReportMetrics metrics={metrics} />
          <ReportKeyHighlights highlights={highlights} />
          <ReportPdfSection report={report} pdf={pdf} cover={cover} />
        </motion.div>

        {report.related?.length > 0 && (
          <div data-testid={SITE.relatedReports} className="border-t border-kira-border pt-10">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-kira-gold mb-5">
              Related Research
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {report.related.map((r) => (
                <ReportCard key={r.id} report={r} />
              ))}
            </div>
          </div>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}
